import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const RATIO_COLORS = {
  'pest365Plus / pest365': '#8b5cf6',
  'oneTime / pest365': '#f59e0b',
  'initialServiceVisit / pest365': '#16a34a',
};

const RATIO_LABELS = {
  'pest365Plus / pest365': 'Plus / Base',
  'oneTime / pest365': 'OneTime / Base',
  'initialServiceVisit / pest365': 'Initial / Base',
};

function getStabilityVerdict(cv) {
  if (cv <= 0.01) return { label: 'Stable', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
  if (cv <= 0.03) return { label: 'Drifting', color: '#d97706', bg: '#fffbeb', border: '#fde68a' };
  return { label: 'Unstable', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
}

const styles = {
  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 12,
    color: '#6b7280',
    margin: 0,
    marginBottom: 16,
  },
  chartContainer: {
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    pointerEvents: 'none',
    background: '#1f2937',
    color: '#f9fafb',
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: 11,
    lineHeight: 1.5,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    zIndex: 10,
    whiteSpace: 'nowrap',
  },
  tooltipDate: {
    fontWeight: 600,
    marginBottom: 4,
    fontSize: 11,
    borderBottom: '1px solid #374151',
    paddingBottom: 4,
  },
  tooltipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
  },
  tooltipSwatch: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  badgeContainer: {
    display: 'flex',
    gap: 8,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    border: '1px solid',
  },
  badgeSwatch: {
    width: 10,
    height: 3,
    borderRadius: 2,
  },
  summaryTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 16,
    fontSize: 12,
  },
  th: {
    textAlign: 'left',
    padding: '6px 10px',
    borderBottom: '2px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  td: {
    padding: '6px 10px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: 14,
  },
};

export default function PriceRatioStabilityChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const ratioKeys = useMemo(() => {
    if (!data || !data.ratios) return [];
    return Object.keys(data.ratios);
  }, [data]);

  useEffect(() => {
    if (!data || !data.dates || !data.ratios || ratioKeys.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 350;
    const margin = { top: 20, right: 130, bottom: 40, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    svg.attr('viewBox', `0 0 ${width} ${height}`);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const dates = data.dates.map((d) => new Date(d));
    const xScale = d3.scaleTime().domain(d3.extent(dates)).range([0, innerWidth]);

    // Compute global y extent across all ratio values with some padding
    let yMin = Infinity;
    let yMax = -Infinity;
    ratioKeys.forEach((key) => {
      const ratioData = data.ratios[key];
      const vals = ratioData.values;
      const localMin = d3.min(vals);
      const localMax = d3.max(vals);
      if (localMin < yMin) yMin = localMin;
      if (localMax > yMax) yMax = localMax;
      // Also consider the +-2% band around mean
      const bandLow = ratioData.mean * 0.98;
      const bandHigh = ratioData.mean * 1.02;
      if (bandLow < yMin) yMin = bandLow;
      if (bandHigh > yMax) yMax = bandHigh;
    });
    const yPad = (yMax - yMin) * 0.1;
    const yScale = d3.scaleLinear().domain([yMin - yPad, yMax + yPad]).range([innerHeight, 0]).nice();

    // Grid lines
    const yTicks = yScale.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .join('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(xScale)
          .ticks(Math.min(dates.length, 6))
          .tickFormat(d3.timeFormat('%b %d'))
      )
      .call((sel) => {
        sel.select('.domain').attr('stroke', '#d1d5db');
        sel.selectAll('.tick line').attr('stroke', '#d1d5db');
        sel.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', 10);
      });

    // Y axis
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(6).tickFormat(d3.format('.2f')))
      .call((sel) => {
        sel.select('.domain').attr('stroke', '#d1d5db');
        sel.selectAll('.tick line').attr('stroke', '#d1d5db');
        sel.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', 10);
      });

    // Draw stability band and lines per ratio
    ratioKeys.forEach((key) => {
      const ratioData = data.ratios[key];
      const color = RATIO_COLORS[key] || '#6b7280';
      const meanVal = ratioData.mean;
      const bandLow = meanVal * 0.98;
      const bandHigh = meanVal * 1.02;

      // Stability band (shaded +-2%)
      g.append('rect')
        .attr('x', 0)
        .attr('y', yScale(bandHigh))
        .attr('width', innerWidth)
        .attr('height', Math.max(0, yScale(bandLow) - yScale(bandHigh)))
        .attr('fill', color)
        .attr('opacity', 0.07);

      // Mean dashed reference line
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', yScale(meanVal))
        .attr('y2', yScale(meanVal))
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,3')
        .attr('opacity', 0.5);

      // Data line
      const lineGen = d3.line()
        .x((_, i) => xScale(dates[i]))
        .y((d) => yScale(d))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(ratioData.values)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('d', lineGen);

      // Data dots
      g.selectAll(`.dot-${key.replace(/[\s\/]/g, '')}`)
        .data(ratioData.values)
        .join('circle')
        .attr('cx', (_, i) => xScale(dates[i]))
        .attr('cy', (d) => yScale(d))
        .attr('r', 3)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);
    });

    // Right-side stability badges
    const badgeStartX = innerWidth + 12;
    ratioKeys.forEach((key, i) => {
      const ratioData = data.ratios[key];
      const color = RATIO_COLORS[key] || '#6b7280';
      const verdict = getStabilityVerdict(ratioData.cv);
      const yPos = 10 + i * 50;

      const badgeG = g.append('g').attr('transform', `translate(${badgeStartX},${yPos})`);

      // Label
      badgeG.append('text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('font-size', 10)
        .attr('fill', '#374151')
        .attr('font-weight', 600)
        .text(RATIO_LABELS[key] || key);

      // Color swatch
      badgeG.append('rect')
        .attr('x', 0)
        .attr('y', 6)
        .attr('width', 18)
        .attr('height', 3)
        .attr('rx', 1.5)
        .attr('fill', color);

      // Stability pill
      const pillWidth = 52;
      const pillHeight = 18;
      badgeG.append('rect')
        .attr('x', 24)
        .attr('y', 0)
        .attr('width', pillWidth)
        .attr('height', pillHeight)
        .attr('rx', 9)
        .attr('fill', verdict.bg)
        .attr('stroke', verdict.border)
        .attr('stroke-width', 1);

      badgeG.append('text')
        .attr('x', 24 + pillWidth / 2)
        .attr('y', pillHeight / 2 + 1)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-size', 9)
        .attr('font-weight', 600)
        .attr('fill', verdict.color)
        .text(verdict.label);
    });

    // Hover overlay for tooltip
    const overlay = g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair');

    const bisect = d3.bisector((d) => d).left;

    overlay.on('mousemove', (event) => {
      const [mx] = d3.pointer(event);
      const hoveredDate = xScale.invert(mx);
      const idx = bisect(dates, hoveredDate);
      const clampedIdx = Math.max(0, Math.min(idx, dates.length - 1));

      // Snap to nearest date
      let nearestIdx = clampedIdx;
      if (clampedIdx > 0) {
        const d0 = dates[clampedIdx - 1];
        const d1 = dates[clampedIdx];
        if (d1 && hoveredDate - d0 > d1 - hoveredDate) {
          nearestIdx = clampedIdx;
        } else {
          nearestIdx = clampedIdx - 1;
        }
      }

      const tipX = xScale(dates[nearestIdx]) + margin.left + 12;
      const tipY = event.offsetY - 10;

      setTooltip({
        x: tipX,
        y: tipY,
        date: data.dates[nearestIdx],
        values: ratioKeys.map((key) => ({
          key,
          label: RATIO_LABELS[key] || key,
          value: data.ratios[key].values[nearestIdx],
          color: RATIO_COLORS[key] || '#6b7280',
        })),
      });

      // Vertical hover line
      g.selectAll('.hover-line').remove();
      g.append('line')
        .attr('class', 'hover-line')
        .attr('x1', xScale(dates[nearestIdx]))
        .attr('x2', xScale(dates[nearestIdx]))
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#9ca3af')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3')
        .attr('pointer-events', 'none');
    });

    overlay.on('mouseleave', () => {
      setTooltip(null);
      g.selectAll('.hover-line').remove();
    });
  }, [data, ratioKeys]);

  if (!data || !data.dates || !data.ratios || Object.keys(data.ratios).length === 0) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Ratio Stability</h3>
        <p style={styles.subtitle}>Are markup ratios between service tiers fixed or drifting?</p>
        <div style={styles.emptyState}>No ratio data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Price Ratio Stability</h3>
      <p style={styles.subtitle}>Are markup ratios between service tiers fixed or drifting?</p>

      <div style={styles.chartContainer}>
        <svg ref={svgRef} style={{ width: '100%', height: 'auto' }} />

        {tooltip && (
          <div
            style={{
              ...styles.tooltip,
              left: tooltip.x,
              top: tooltip.y,
            }}
          >
            <div style={styles.tooltipDate}>{tooltip.date}</div>
            {tooltip.values.map((v) => (
              <div key={v.key} style={styles.tooltipRow}>
                <span style={{ ...styles.tooltipSwatch, backgroundColor: v.color }} />
                <span>{v.label}:</span>
                <span style={{ fontWeight: 600 }}>
                  {v.value != null ? v.value.toFixed(3) : 'N/A'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.badgeContainer}>
        {ratioKeys.map((key) => {
          const ratioData = data.ratios[key];
          const verdict = getStabilityVerdict(ratioData.cv);
          const color = RATIO_COLORS[key] || '#6b7280';
          return (
            <span
              key={key}
              style={{
                ...styles.badge,
                backgroundColor: verdict.bg,
                borderColor: verdict.border,
                color: verdict.color,
              }}
            >
              <span style={{ ...styles.badgeSwatch, backgroundColor: color }} />
              {RATIO_LABELS[key] || key}: {verdict.label}
            </span>
          );
        })}
      </div>

      <table style={styles.summaryTable}>
        <thead>
          <tr>
            <th style={styles.th}>Ratio</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Mean</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>CV</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Stability</th>
          </tr>
        </thead>
        <tbody>
          {ratioKeys.map((key) => {
            const ratioData = data.ratios[key];
            const verdict = getStabilityVerdict(ratioData.cv);
            const color = RATIO_COLORS[key] || '#6b7280';
            return (
              <tr key={key}>
                <td style={styles.td}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 10,
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: color,
                        display: 'inline-block',
                      }}
                    />
                    {RATIO_LABELS[key] || key}
                  </span>
                </td>
                <td style={{ ...styles.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {ratioData.mean.toFixed(3)}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {ratioData.cv.toFixed(4)}
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: verdict.bg,
                      color: verdict.color,
                      border: `1px solid ${verdict.border}`,
                    }}
                  >
                    {verdict.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
